import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Download, ImageOff, Clock } from "lucide-react";
import { Header } from "@/components/Header";
import { api, mediaUrl, apiError } from "@/lib/api";

const SCOPE_LABELS = {
  cabinets: "Cabinets", countertop: "Countertop", backsplash: "Backsplash",
  hardware: "Hardware", sink: "Sink", faucet: "Faucet", adjustment: "Adjustment",
};

export default function History() {
  const [renders, setRenders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/renders");
      setRenders(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/renders/${id}`);
      setRenders((p) => p.filter((r) => r.id !== id));
      toast.success("Removed");
    } catch { toast.error("Could not remove."); }
  };

  const download = async (url, id) => {
    const a = document.createElement("a");
    a.href = mediaUrl(url);
    a.download = `render-${id}.png`;
    a.target = "_blank";
    a.click();
  };

  const fmt = (iso) => new Date(iso).toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });

  return (
    <div className="min-h-screen">
      <Header />
      <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
        <p className="overline">Presentation</p>
        <h1 className="font-display text-3xl tracking-tight md:text-4xl">Render History</h1>
        <p className="mt-2 text-sm text-[var(--ink-soft)]">Your last 30 renders, newest first. Pull any one back up in front of a customer.</p>

        {loading ? (
          <p className="mt-8 text-sm text-[var(--ink-soft)]">Loading…</p>
        ) : renders.length === 0 ? (
          <div className="mt-10 flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-[var(--line)] py-20 text-[var(--ink-soft)]">
            <ImageOff className="h-10 w-10" strokeWidth={1.25} />
            <p className="text-sm">No renders yet — create one in the Studio.</p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {renders.map((r) => (
              <div key={r.id} data-testid={`render-${r.id}`} className="overflow-hidden rounded-xl border border-[var(--line)] bg-white shadow-sm">
                <div className="aspect-[4/3] overflow-hidden bg-[var(--bone)]">
                  <img src={mediaUrl(r.after_url)} alt="Generated render" className="h-full w-full object-cover" />
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-[var(--ink-soft)]">
                    <Clock className="h-3.5 w-3.5" /> {fmt(r.created_at)}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {(r.scopes || []).map((s) => (
                      <span key={s} className="rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[10px] font-medium">{SCOPE_LABELS[s] || s}</span>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <button data-testid={`download-render-${r.id}`} onClick={() => download(r.after_url, r.id)}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-[var(--line)] py-2 text-xs font-medium transition-colors hover:bg-[var(--bone)]">
                      <Download className="h-3.5 w-3.5" /> Download
                    </button>
                    <button data-testid={`delete-render-${r.id}`} onClick={() => remove(r.id)}
                      className="rounded-full border border-[var(--line)] p-2 text-[var(--brand)] transition-colors hover:bg-[var(--surface-2)]">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
